/**
 * Cross-origin dedup for funnel events (layout :8080 vs checkout :8082 share localhost cookies).
 * sessionStorage is origin-scoped; cookies with path=/ dedupe across ports on the same host.
 */

const CHECKOUT_DEDUP_COOKIE = 'te_bc';
const ORDER_DEDUP_COOKIE = 'te_po';
const IDENTITY_DEDUP_COOKIE = 'te_id';
const ADD_TO_CART_DEDUP_MS = 2000;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=86400; SameSite=Lax`;
  } catch {
    // ignore cookie write failures
  }
}

export function isCheckoutAlreadyTracked(cartToken: string): boolean {
  return readCookie(CHECKOUT_DEDUP_COOKIE) === cartToken;
}

export function markCheckoutTracked(cartToken: string): void {
  writeCookie(CHECKOUT_DEDUP_COOKIE, cartToken);
}

export function isPurchaseAlreadyTracked(orderId: string): boolean {
  return readCookie(ORDER_DEDUP_COOKIE) === orderId;
}

export function markPurchaseTracked(orderId: string): void {
  writeCookie(ORDER_DEDUP_COOKIE, orderId);
}

/** Cross-origin dedup for identify_user (layout vs checkout share localhost cookies). */
export function isIdentityAlreadyTracked(identityKey: string): boolean {
  return readCookie(IDENTITY_DEDUP_COOKIE) === identityKey;
}

export function markIdentityTracked(identityKey: string): void {
  writeCookie(IDENTITY_DEDUP_COOKIE, identityKey);
}

const recentAddToCartKeys = new Map<string, number>();

/** Suppress duplicate add_to_cart within a short window (double-click / dual hooks). */
export function shouldSkipAddToCartDedup(productId: string, variantId: string, quantity: number): boolean {
  if (typeof window === 'undefined') return false;

  const key = `${productId}:${variantId}:${quantity}`;
  const now = Date.now();
  const last = recentAddToCartKeys.get(key);
  if (last != null && now - last < ADD_TO_CART_DEDUP_MS) {
    return true;
  }

  recentAddToCartKeys.set(key, now);
  return false;
}

/** Test-only: clears in-memory add_to_cart dedup window between Jest cases. */
export function resetAddToCartDedupForTests(): void {
  recentAddToCartKeys.clear();
}
